import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

import useAuthStore from '@entities/auth/model/useAuthStore';

import {
  useGetEmployeesByTeamQuery,
  useGetEmployeeByNameQuery,
  useGetEmployeesByCompanyQuery,
} from '@features/employee/hooks/index';
import useGetTeamQuery from '@features/team/hooks';
import { useRejectListQuery } from '@features/company/hooks';
import Alert from '@shared/components/atoms/Alert';

import OrganizationTree from '../OrganizationTree/OrganizationTree';
import { sortOrganizationTree } from '@features/document-write/utils/sortOrganizationTree';

import styles from './RecipientModal.module.scss';

/**
 * RecipientModal 컴포넌트
 * - 수신자/시행자 선택 모달
 * - ApprovalLineModal과 동일한 UI/UX로 리팩토링
 * - 역할 선택 기능 없음 (번호 | 이름 | 부서 표시)
 */
function RecipientModal({
  isOpen,
  onClose,
  onSelect,
  title,
  selectedRecipients = [],
  applyRejectFilter = true,
}) {
  const [selectedTeamCode, setSelectedTeamCode] = useState(null);
  const [isCompanySelected, setIsCompanySelected] = useState(false); // 회사(최상위 조직) 선택 상태
  const [searchName, setSearchName] = useState('');
  const [searchTrigger, setSearchTrigger] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null); // 드래그 오버 중인 인덱스
  const [dragOverPosition, setDragOverPosition] = useState(null); // 'top' | 'bottom'
  const [checkedEmployees, setCheckedEmployees] = useState(new Set());
  const [alertState, setAlertState] = useState({
    isOpen: false,
    message: '',
  });
  // 표시되거나 검색되어야할 데이터 필터
  function filterEmployees(source) {
    let result = source;

    // 시행자 선택일 때 notEnforcer 제외
    if (title === '시행자 선택') {
      result = result.filter((emp) => emp.notEnforcer !== true);
    }

    // reject 필터
    if (applyRejectFilter && rejectEnumberSet?.size) {
      result = result.filter((emp) => !rejectEnumberSet.has(emp.enumber));
    }

    return result;
  }

  const MAX_SELECTED_EMPLOYEES = 20; // 참조자/시행자는 넉넉하게 20명으로 설정 (필요시 조정)

  // 모달 오픈 시 배경(body) 스크롤 방지
  useEffect(
    function () {
      if (!isOpen) {
        return undefined;
      }

      const currentCount = Number(document.body.dataset.modalLockCount || '0');
      const nextCount = currentCount + 1;
      document.body.dataset.modalLockCount = String(nextCount);

      if (currentCount === 0) {
        document.body.dataset.modalLockOverflow = document.body.style.overflow || '';
        document.body.dataset.modalLockPaddingRight = document.body.style.paddingRight || '';

        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (scrollBarWidth > 0) {
          document.body.style.paddingRight = `${scrollBarWidth}px`;
        }
      }

      return function () {
        const count = Number(document.body.dataset.modalLockCount || '0');
        const restoredCount = Math.max(0, count - 1);
        document.body.dataset.modalLockCount = String(restoredCount);

        if (restoredCount === 0) {
          document.body.style.overflow = document.body.dataset.modalLockOverflow || '';
          document.body.style.paddingRight = document.body.dataset.modalLockPaddingRight || '';
          delete document.body.dataset.modalLockOverflow;
          delete document.body.dataset.modalLockPaddingRight;
        }
      };
    },
    [isOpen],
  );

  // Alert 표시 함수
  function showAlert(message) {
    setAlertState({
      isOpen: true,
      message,
    });
  }

  // Alert 닫기 함수
  function closeAlert() {
    setAlertState({
      isOpen: false,
      message: '',
    });
  }

  // 회사 코드 가져오기 (Zustand store에서)
  const companyCode = useAuthStore((state) => state.companyCode);
  // 부서 코드 가져오기 (Zustand store에서)
  const teamCode = useAuthStore((state) => state.user.teamCode);
  const { data: rejectList, isLoading } = useRejectListQuery({
    company: companyCode,
    teamCode: teamCode,
  });
  const rejectEnumberSet = useMemo(() => {
    if (!applyRejectFilter) {
      return null;
    }
    if (!rejectList || rejectList.length === 0) {
      return new Set();
    }

    return new Set(rejectList.map((emp) => emp.enumber));
  }, [rejectList]);
  // 팀 목록 조회
  const teamQuery = useGetTeamQuery(companyCode, {
    enabled: isOpen && !!companyCode,
  });
  const teamList = teamQuery.data?.resultData?.teamList || [];

  // 팀별 사원 목록 조회
  const employeeQuery = useGetEmployeesByTeamQuery(selectedTeamCode, {
    enabled: isOpen && !!selectedTeamCode && !isCompanySelected,
  });
  const teamEmployees = employeeQuery.data?.resultData?.employeeList || [];

  // 회사(최상위 조직) 선택 시 대표이사 조회
  const companyEmployeeQuery = useGetEmployeesByCompanyQuery(companyCode, {
    enabled: isOpen && isCompanySelected && !!companyCode,
  });
  const companyEmployeesRaw = companyEmployeeQuery.data?.resultData?.employeeList || [];
  // 대표이사만 필터링 (직책이 "대표이사"인 직원만)
  const companyEmployees = companyEmployeesRaw.filter(function (emp) {
    return emp.position === '대표이사' || emp.position === '대표';
  });

  // 현재 표시할 직원 목록 (회사 선택 시 대표이사, 팀 선택 시 팀원)
  const employees = useMemo(() => {
    const source = isCompanySelected ? companyEmployees : teamEmployees;
    return filterEmployees(source);
  }, [
    isCompanySelected,
    companyEmployees,
    teamEmployees,
    title,
    applyRejectFilter,
    rejectEnumberSet,
  ]);

  // 사원 이름 검색
  const employeeNameQuery = useGetEmployeeByNameQuery(searchTrigger, {
    enabled: isOpen && searchTrigger.length >= 1,
  });
  const searchResults = employeeNameQuery.data?.resultData?.employeeList || [];
  const teamNameToCodeMap = useMemo(() => {
    const map = new Map();
    teamList.forEach((team) => {
      if (team.team && team.teamCode) {
        map.set(team.team, team.teamCode);
      }
    });
    return map;
  }, [teamList]);

  // 모달이 열릴 때만 selectedRecipients로 초기화, 닫힐 때만 초기화 (의존성은 isOpen만 사용해 무한 루프 방지)
  // - selectedRecipients/teamNameToCodeMap을 의존성에 넣으면 부모가 매 렌더 새 참조를 넘길 때 effect가 반복 실행됨
  useEffect(
    function () {
      if (isOpen) {
        const recipients =
          selectedRecipients && selectedRecipients.length > 0 ? selectedRecipients : [];
        const updatedEmployees = recipients.map(function (r) {
          return {
            enumber: r.enumber || 0,
            name: r.name || '',
            position: r.position || '',
            team: r.team || '',
            teamCode: r.teamCode ?? teamNameToCodeMap.get(r.team) ?? undefined,
          };
        });
        setSelectedEmployees(updatedEmployees);
        setCheckedEmployees(new Set(updatedEmployees.map((emp) => emp.enumber)));
      } else {
        setSelectedTeamCode(null);
        setIsCompanySelected(false);
        setSearchName('');
        setSearchTrigger('');
        setSelectedEmployees([]);
        setCheckedEmployees(new Set());
      }
    },
    [isOpen],
  );
  const sortedTeamList = useMemo(
    function () {
      return sortOrganizationTree(teamList);
    },
    [teamList],
  );
  // 팀 선택 핸들러
  function handleTeamSelect(teamCode) {
    setSelectedTeamCode(teamCode);
    setIsCompanySelected(false); // 팀 선택 시 회사 선택 해제
    setSearchName('');
    setSearchTrigger('');
  }

  // 회사(최상위 조직) 선택 핸들러
  function handleCompanySelect() {
    setSelectedTeamCode(null); // 팀 선택 해제
    setIsCompanySelected(true);
    setSearchName('');
    setSearchTrigger('');
  }

  // 중복 직원 확인
  function checkDuplicateEmployee(employee) {
    return selectedEmployees.some((emp) => emp.enumber === employee.enumber);
  }

  // 체크박스 토글
  function handleCheckboxToggle(employee) {
    const isChecked = checkedEmployees.has(employee.enumber);
    const isInSelected = selectedEmployees.some((emp) => emp.enumber === employee.enumber);

    if (isChecked) {
      // 체크 해제: 목록에서 제거
      setCheckedEmployees(function (prev) {
        const newChecked = new Set(prev);
        newChecked.delete(employee.enumber);
        return newChecked;
      });
      setSelectedEmployees(function (prev) {
        return prev.filter((emp) => emp.enumber !== employee.enumber);
      });
    } else {
      // 체크: 목록에 추가
      // 이미 selectedEmployees에 있으면 추가하지 않음 (중복 방지)
      if (isInSelected) {
        // 체크박스만 체크 상태로 변경
        setCheckedEmployees(function (prev) {
          const newChecked = new Set(prev);
          newChecked.add(employee.enumber);
          return newChecked;
        });
        return;
      }

      if (selectedEmployees.length >= MAX_SELECTED_EMPLOYEES) {
        showAlert(`${MAX_SELECTED_EMPLOYEES}명 이상 추가할 수 없습니다.`);
        return;
      }

      if (checkDuplicateEmployee(employee)) {
        showAlert('이미 선택된 사원입니다.');
        return;
      }

      const newEmployee = {
        enumber: employee.enumber,
        name: employee.name,
        position: employee.position || '',
        team: employee.team || '',
        teamCode: employee.teamCode,
      };

      setCheckedEmployees(function (prev) {
        const newChecked = new Set(prev);
        newChecked.add(employee.enumber);
        return newChecked;
      });
      setSelectedEmployees(function (prev) {
        // 중복 확인을 한 번 더 수행 (상태 업데이트가 비동기적이므로)
        const alreadyExists = prev.some((emp) => emp.enumber === employee.enumber);
        if (alreadyExists) {
          return prev;
        }
        return [...prev, newEmployee];
      });
    }
  }

  // 직원 리스트에서 드래그 시작
  function handleEmployeeDragStart(e, employee) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify(employee));
    e.target.style.opacity = '0.5';
  }

  // 직원 리스트에서 드래그 종료
  function handleEmployeeDragEnd(e) {
    e.target.style.opacity = '1';
  }

  // 선택 목록 영역에 드롭
  function handleDropToSelectedSection(e) {
    e.preventDefault();
    const employeeData = e.dataTransfer.getData('application/json');
    if (!employeeData) {
      return;
    }

    try {
      const employee = JSON.parse(employeeData);
      // 중복 확인
      if (checkDuplicateEmployee(employee)) {
        return;
      }

      // 최대 인원수 확인
      if (selectedEmployees.length >= MAX_SELECTED_EMPLOYEES) {
        showAlert(`${MAX_SELECTED_EMPLOYEES}명 이상 추가할 수 없습니다.`);
        return;
      }

      const newEmployee = {
        enumber: employee.enumber,
        name: employee.name,
        position: employee.position || '',
        team: employee.team || '',
        teamCode: employee.teamCode,
      };

      // selectedEmployees에 추가
      setSelectedEmployees(function (prev) {
        return [...prev, newEmployee];
      });

      // 체크박스도 체크 상태로 업데이트
      setCheckedEmployees(function (prev) {
        const newChecked = new Set(prev);
        newChecked.add(employee.enumber);
        return newChecked;
      });
    } catch (error) {
      console.error('Failed to parse employee data:', error);
    }
  }

  // 선택된 직원 제거
  function handleRemoveEmployee(index) {
    const employeeToRemove = selectedEmployees[index];
    setSelectedEmployees(function (prev) {
      return prev.filter((_, i) => i !== index);
    });
    setCheckedEmployees(function (prev) {
      const newChecked = new Set(prev);
      newChecked.delete(employeeToRemove.enumber);
      return newChecked;
    });
  }

  // 드래그 시작 (순서 변경용)
  function handleDragStart(e, index) {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  }

  // 드래그 종료
  function handleDragEnd(e) {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 드래그 오버 (드롭 가능 영역 표시)
  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  // 카드 위에서 드래그 오버 (위/아래 구분)
  function handleCardDragOver(e, index) {
    e.preventDefault();
    e.stopPropagation();

    // 드래그 중이 아니면 무시
    if (draggedIndex === null) {
      return;
    }

    // 같은 카드 위에서는 무시
    if (draggedIndex === index) {
      setDragOverIndex(null);
      setDragOverPosition(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const cardMiddle = rect.top + rect.height / 2;

    // 마우스가 카드의 위쪽 절반에 있으면 'top', 아래쪽 절반에 있으면 'bottom'
    const position = mouseY < cardMiddle ? 'top' : 'bottom';

    setDragOverIndex(index);
    setDragOverPosition(position);
  }

  // 카드에서 드래그 리브 (드래그가 카드 밖으로 나갔을 때)
  function handleCardDragLeave(e) {
    // 자식 요소로 이동한 경우는 무시
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 드롭 (순서 변경)
  function handleDrop(e, dropIndex) {
    e.preventDefault();
    if (draggedIndex === null) {
      return;
    }

    // 같은 위치에 드롭하면 무시
    if (draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setDragOverPosition(null);
      return;
    }

    const updated = [...selectedEmployees];
    const draggedItem = updated[draggedIndex];

    // 드래그된 아이템 제거
    updated.splice(draggedIndex, 1);

    // dragOverPosition에 따라 삽입 위치 결정
    let insertIndex = dropIndex;
    if (dragOverPosition === 'bottom') {
      // 아래에 드롭: dropIndex 다음 위치에 삽입
      insertIndex = dropIndex + 1;
    }
    // 위에 드롭: dropIndex 위치에 삽입 (이미 insertIndex = dropIndex)

    // 드롭 위치에 삽입
    updated.splice(insertIndex, 0, draggedItem);

    setSelectedEmployees(updated);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 검색 핸들러
  function handleSearch() {
    setSearchTrigger(searchName);
    setSelectedTeamCode(null);
  }

  // 저장 핸들러
  function handleSave() {
    if (onSelect) {
      onSelect(selectedEmployees);
    }
    onClose();
  }
  // 제외할 검색 데이터 제외
  const filteredSearchResults = useMemo(() => {
    return filterEmployees(searchResults);
  }, [searchResults, title, applyRejectFilter, rejectEnumberSet]);
  // 검색 결과 표시 여부
  const showSearchResults = searchTrigger.length >= 1;
  // const displayEmployees = showSearchResults ? searchResults : employees;
  const displayEmployees = showSearchResults ? filteredSearchResults : employees;

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} role="dialog" aria-modal="true">
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{title || '수신자 선택'}</h2>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.contentLayout}>
            {/* 왼쪽: 조직도 트리 */}
            <div className={`${styles.columnContainer} ${styles.treeSection}`}>
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>조직도</div>
              </div>
              <div className={styles.treeContent}>
                <OrganizationTree
                  companyName={teamList?.[0]?.company || '링크아이'}
                  teamList={sortedTeamList}
                  selectedTeamCode={selectedTeamCode}
                  onSelectTeam={handleTeamSelect}
                  onSelectCompany={handleCompanySelect}
                  isCompanySelected={isCompanySelected}
                  height={510}
                />
              </div>
            </div>

            {/* 가운데: 임직원 목록 */}
            <div className={`${styles.columnContainer} ${styles.employeeSection}`}>
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>임직원</div>
                <div className={styles.searchBlock}>
                  <div className={styles.searchRow}>
                    <div className={styles.searchLabelCell}>이름</div>
                    <div className={styles.searchControlCell}>
                      <input
                        type="text"
                        className={styles.searchInput}
                        style={{ width: '130px' }}
                        value={searchName}
                        onChange={function (e) {
                          setSearchName(e.target.value);
                        }}
                        onKeyDown={function (e) {
                          if (e.key === 'Enter') {
                            handleSearch();
                          }
                        }}
                      />
                      <button type="button" className={styles.smallButton} onClick={handleSearch}>
                        검색
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 직원 목록 테이블 */}
              <div className={styles.employeeTableBlock}>
                <div className={styles.employeeTableSection}>
                  <table className={styles.employeeTable}>
                    <thead>
                      <tr>
                        <th className={styles.thCenter}>
                          <input
                            type="checkbox"
                            checked={
                              displayEmployees.length > 0 &&
                              displayEmployees.every((emp) => checkedEmployees.has(emp.enumber))
                            }
                            onChange={function (e) {
                              if (e.target.checked) {
                                const newChecked = new Set(checkedEmployees);
                                const newEmployees = [...selectedEmployees];

                                displayEmployees.forEach((emp) => {
                                  // 이미 체크된 직원은 건너뛰기
                                  if (newChecked.has(emp.enumber)) {
                                    return;
                                  }
                                  // 중복 확인 (selectedEmployees에 이미 있는지)
                                  if (!checkDuplicateEmployee(emp)) {
                                    if (newEmployees.length < MAX_SELECTED_EMPLOYEES) {
                                      newChecked.add(emp.enumber);
                                      newEmployees.push({
                                        enumber: emp.enumber,
                                        name: emp.name,
                                        position: emp.position || '',
                                        team: emp.team || '',
                                        teamCode: emp.teamCode,
                                      });
                                    }
                                  } else {
                                    // 이미 selectedEmployees에 있으면 체크만 추가
                                    newChecked.add(emp.enumber);
                                  }
                                });
                                setCheckedEmployees(newChecked);
                                setSelectedEmployees(newEmployees);
                              } else {
                                const currentEnumbers = displayEmployees.map((emp) => emp.enumber);
                                setCheckedEmployees(function (prev) {
                                  const next = new Set(prev);
                                  currentEnumbers.forEach(function (num) {
                                    next.delete(num);
                                  });
                                  return next;
                                });
                                setSelectedEmployees(function (prev) {
                                  return prev.filter(
                                    (emp) => !currentEnumbers.includes(emp.enumber),
                                  );
                                });
                              }
                            }}
                          />
                        </th>
                        <th className={styles.thCenter}>부서</th>
                        <th className={styles.thCenter}>직급</th>
                        <th className={styles.thCenter}>이름</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayEmployees.length > 0 ? (
                        displayEmployees.map(function (employee) {
                          return (
                            <tr
                              key={employee.enumber}
                              className={styles.employeeRow}
                              draggable
                              onDragStart={function (e) {
                                handleEmployeeDragStart(e, employee);
                              }}
                              onDragEnd={handleEmployeeDragEnd}
                              onClick={function (e) {
                                // 체크박스 클릭은 제외 (onChange에서 처리)
                                if (e.target.type === 'checkbox') {
                                  return;
                                }
                                handleCheckboxToggle(employee);
                              }}
                            >
                              <td className={styles.tdCenter}>
                                <input
                                  type="checkbox"
                                  checked={checkedEmployees.has(employee.enumber)}
                                  onChange={function (e) {
                                    e.stopPropagation();
                                    handleCheckboxToggle(employee);
                                  }}
                                  onClick={function (e) {
                                    e.stopPropagation();
                                  }}
                                />
                              </td>
                              <td className={styles.tdCenter}>{employee.team || '-'}</td>
                              <td className={styles.tdCenter}>{employee.position || '-'}</td>
                              <td className={styles.tdCenter}>{employee.name}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className={styles.emptyCell}>
                            {showSearchResults
                              ? '검색 결과가 없습니다.'
                              : '부서를 선택하거나 검색하세요.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* 오른쪽: 선택된 목록 */}
            <div
              className={`${styles.columnContainer} ${styles.selectedSection}`}
              onDragOver={handleDragOver}
              onDrop={handleDropToSelectedSection}
            >
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>{title?.replace(' 선택', '') || '수신자'}</div>
              </div>
              <div className={styles.selectedList}>
                {selectedEmployees.length > 0 ? (
                  selectedEmployees.map(function (employee, index) {
                    const showTopLine =
                      draggedIndex !== null &&
                      dragOverIndex === index &&
                      dragOverPosition === 'top';
                    const showBottomLine =
                      draggedIndex !== null &&
                      dragOverIndex === index &&
                      dragOverPosition === 'bottom';

                    return (
                      <div key={`${employee.enumber}-${index}`} className={styles.cardWrapper}>
                        {/* 위쪽 파란색 선 */}
                        {showTopLine && <div className={styles.dropIndicator} />}
                        <div
                          className={styles.approverCard}
                          draggable
                          onDragStart={function (e) {
                            handleDragStart(e, index);
                          }}
                          onDragOver={function (e) {
                            handleCardDragOver(e, index);
                          }}
                          onDragLeave={handleCardDragLeave}
                          onDragEnd={handleDragEnd}
                          onDrop={function (e) {
                            handleDrop(e, index);
                          }}
                        >
                          <span className={styles.cardIndex}>{index + 1}</span>
                          <span className={styles.approverName}>{employee.name}</span>
                          <span className={styles.approverTeam}>{employee.team || '-'}</span>
                          <button
                            type="button"
                            className={styles.removeButton}
                            onClick={function () {
                              handleRemoveEmployee(index);
                            }}
                            aria-label="제거"
                          >
                            ✕
                          </button>
                        </div>
                        {/* 아래쪽 파란색 선 */}
                        {showBottomLine && <div className={styles.dropIndicator} />}
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.emptyCell}>선택된 직원이 없습니다.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={`${styles.footerButton} ${styles.btnCancel}`}
            onClick={onClose}
          >
            취소
          </button>
          <button
            type="button"
            className={`${styles.footerButton} ${styles.btnSave}`}
            onClick={handleSave}
          >
            저장
          </button>
        </div>
      </div>

      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
    </div>,
    document.body,
  );
}

export default RecipientModal;
